import { createClient } from '@supabase/supabase-js';

// Supabase connection configuration
const supabaseUrl = "https://htybpvxpqqtztncwkiyx.supabase.co";

// Original anonymous key (limited permissions, respects RLS)
const supabaseAnonKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0eWJwdnhwcXF0enRuY3draXl4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUwNTE5NjAsImV4cCI6MjA2MDYyNzk2MH0.KGhqH4bGQRLOujdeAxwJt3ndnDosH8HgAgiR1iOTPsw";

// Service role key (bypasses RLS policies - only use server-side)
// Replace this with your actual service role key from Supabase dashboard
const supabaseServiceRoleKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh0eWJwdnhwcXF0enRuY3draXl4Iiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc0NTA1MTk2MCwiZXhwIjoyMDYwNjI3OTYwfQ.0xWB8vPuWwe-CUFz0crA2kDfesthiOXnlsHYbhawGT4";

// IMPORTANT: Choose which key to use based on your needs:
// - For client-side code that should respect RLS policies, use supabaseAnonKey
// - For server-side code that needs full access, use supabaseServiceRoleKey
// Using service role key in client-side code is a security risk!
// For testing purposes, we'll use the service role key to verify data access

// To use anonymous key (default with RLS):
// const supabase = createClient(supabaseUrl, supabaseAnonKey);

// To use service role key (bypasses RLS):
const supabase = createClient(supabaseUrl, supabaseServiceRoleKey);

// Initialize authentication state
let isAuthenticated = false;

// Authenticate with Supabase using email and password
export const authenticate = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error('Authentication error:', error.message);
      isAuthenticated = false;
      throw error;
    }
    
    isAuthenticated = true;
    console.log('Successfully authenticated as:', data.user.email);
    return data;
  } catch (error) {
    console.error('Error during authentication:', error.message);
    throw error;
  }
};

// Check if the current session is valid
export const checkSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      isAuthenticated = false;
      throw error;
    }
    
    isAuthenticated = data.session !== null;
    return isAuthenticated;
  } catch (error) {
    console.error('Session check error:', error.message);
    return false;
  }
};

// Helper functions for common database operations
export const fetchData = async (table, options = {}, autoAuthenticate = false) => {
  try {
    // Check authentication status if autoAuthenticate is true
    if (autoAuthenticate) {
      const sessionValid = await checkSession();
      if (!sessionValid && options.credentials) {
        await authenticate(options.credentials.email, options.credentials.password);
      }
    }
    
    let query = supabase.from(table).select(options.select || '*');

    // Apply filters if provided
    if (options.filters) {
      options.filters.forEach(filter => {
        const { column, operator, value } = filter;
        query = query.filter(column, operator, value);
      });
    }

    // Apply ordering if provided
    if (options.orderBy) {
      const { column, ascending } = options.orderBy;
      query = query.order(column, { ascending });
    }

    // Apply pagination if provided
    if (options.limit) {
      query = query.limit(options.limit);
    }

    if (options.offset) {
      query = query.offset(options.offset);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error(`Error fetching data from ${table}:`, error);
    throw error;
  }
};

export const insertData = async (table, data) => {
  try {
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select();
      
    if (error) throw error;
    return result;
  } catch (error) {
    console.error(`Error inserting data into ${table}:`, error);
    throw error;
  }
};

export const updateData = async (table, id, data) => {
  try {
    const { data: result, error } = await supabase
      .from(table)
      .update(data)
      .eq('id', id)
      .select();
      
    if (error) throw error;
    return result;
  } catch (error) {
    console.error(`Error updating data in ${table}:`, error);
    throw error;
  }
};

export const deleteData = async (table, id) => {
  try {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);
      
    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Error deleting data from ${table}:`, error);
    throw error;
  }
};

// Enhanced authentication helpers
export const signInWithEmail = async (email, password) => {
  return authenticate(email, password);
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    isAuthenticated = false;
    console.log('Successfully signed out');
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Get current authenticated user
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error('Error getting user:', error.message);
      throw error;
    }
    
    return data.user;
  } catch (error) {
    console.error('Error getting current user:', error);
    throw error;
  }
};

// Example authentication function for accessing protected tables
export const authenticatedQuery = async (table, email, password) => {
  try {
    // Authenticate first
    await authenticate(email, password);
    
    // Then fetch data
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .limit(10);
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error(`Error in authenticated query to ${table}:`, error);
    throw error;
  }
};

// Real-time subscriptions
export const subscribeToTable = (table, callback) => {
  return supabase
    .channel(`public:${table}`)
    .on('postgres_changes', { event: '*', schema: 'public', table }, payload => {
      callback(payload);
    })
    .subscribe();
};

// Export the supabase client directly for advanced use cases
export default supabase;